import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { PAGE_TITLES } from "~/configs/pageTitles";
import { useSiteContext } from "~/contexts/SiteContext";
import type { WizardStep } from "~/contexts/WizardContext";
import { routes } from "~/router";

/**
 * Global (site-agnostic) URL builders for the Visibility Engine domains module.
 *
 * Use these when a link must always resolve to the global module, regardless of
 * the context it was opened from — e.g. the domains list, where a domain may
 * belong to a different site than the one in the current URL, so keeping the
 * site context would produce an inconsistent `/sites/:id/...` link.
 */
export const globalVisibilityEngineRoutes = (() => {
  const domain = (domainId: string | number) =>
    routes.visibilityEngine.domain({ domainId });

  return {
    domains: () => routes.visibilityEngine.domains(),
    domain,
    wizardStep: (domainId: string | number, step: WizardStep) =>
      `${domain(domainId)}/wizard/${step}`,
  };
})();

/**
 * Context-aware URL builders for the Visibility Engine domains module.
 *
 * The module is mounted both globally (`/visibility-engine/...`) and inside a
 * site (`/sites/:id/visibility-engine/...`). Every in-module link/redirect must
 * stay within the context it was opened from, so the builders are derived from
 * the `:id` route param: present → site context, absent → global context.
 */
export function useVisibilityEngineRoutes() {
  const { id: siteId } = useParams();
  const delegatedDomainId = useSiteContext()?.site?.site?.delegated_domain?.id;

  return useMemo(() => {
    if (siteId) {
      const domain = (domainId: string | number) =>
        routes.site.visibilityEngine.domain({ id: siteId, domainId });

      return {
        // No domains list in the site context — the module deals only with the
        // site's own delegated domain, so "domains" resolves to its detail page
        // (falling back to About until the site has loaded).
        domains: () =>
          delegatedDomainId != null
            ? domain(delegatedDomainId)
            : routes.site.visibilityEngine.about({ id: siteId }),
        domain,
        wizardStep: (domainId: string | number, step: WizardStep) =>
          `${domain(domainId)}/wizard/${step}`,
      };
    }

    return globalVisibilityEngineRoutes;
  }, [siteId, delegatedDomainId]);
}

/**
 * Breadcrumbs for the About page. The page is module-level (not tied to a
 * specific site section), so the trail skips the site crumb.
 */
export function useAboutBreadcrumbs() {
  const { id: siteId } = useParams();

  return useMemo(() => {
    if (!siteId) return null;

    return [
      { title: PAGE_TITLES.dashboard, link: routes.home() },
      {
        title: PAGE_TITLES.aboutAiVisibilityEngine,
        link: routes.site.visibilityEngine.about({ id: siteId }),
      },
    ];
  }, [siteId]);
}

/**
 * Breadcrumbs for the Configuration pages (domain detail / wizard).
 *
 * Only rendered in the site context, where they read `Dashboard / {site} /
 * Configuration` so the user can navigate back to the site the module was
 * opened from. In the global context the module is reached on its own, so no
 * breadcrumbs are shown — the hook returns `null`.
 */
export function useConfigurationBreadcrumbs() {
  const { id: siteId, domainId } = useParams();
  const siteName = useSiteContext()?.site?.site?.name;

  return useMemo(() => {
    if (!siteId) return null;

    return [
      { title: "Dashboard", link: routes.home() },
      { title: siteName ?? "", link: routes.site.root({ id: siteId }) },
      {
        title: "Configuration",
        link: domainId
          ? routes.site.visibilityEngine.domain({ id: siteId, domainId })
          : routes.site.visibilityEngine.about({ id: siteId }),
      },
    ];
  }, [siteId, domainId, siteName]);
}
